import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Grid,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Switch,
  FormControlLabel,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import apiClient from '../../services/api';

const EMPTY_FORM = {
  title: '',
  subtitle: '',
  description: '',
  ctaText: '',
  ctaLink: '',
  orderIndex: 0,
  isActive: true,
};

const ManageHeroSlides = () => {
  const [slides, setSlides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [openDialog, setOpenDialog] = useState(false);
  const [editingSlide, setEditingSlide] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [imageFile, setImageFile] = useState(null);
  const [saving, setSaving] = useState(false);

  const loadSlides = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const result = await apiClient.getHeroSlides();
      if (result && result.success) {
        setSlides(result.data);
      } else {
        setError(result?.error || 'Failed to load slides');
      }
    } catch (e) {
      setError(e.message || 'Failed to load slides');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSlides();
  }, [loadSlides]);

  const handleOpenDialog = (slide = null) => {
    if (slide) {
      setEditingSlide(slide);
      setFormData({
        title: slide.title || '',
        subtitle: slide.subtitle || '',
        description: slide.description || '',
        ctaText: slide.ctaText || '',
        ctaLink: slide.ctaLink || '',
        orderIndex: slide.orderIndex || 0,
        isActive: slide.isActive !== false,
      });
    } else {
      setEditingSlide(null);
      setFormData({ ...EMPTY_FORM, orderIndex: slides.length + 1 });
    }
    setImageFile(null);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingSlide(null);
    setFormData(EMPTY_FORM);
    setImageFile(null);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: name === 'orderIndex' ? Number(value) : value,
    }));
  };

  const handleSwitchChange = (e) => {
    const { name, checked } = e.target;
    setFormData((prev) => ({ ...prev, [name]: checked }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files && e.target.files[0] ? e.target.files[0] : null;
    setImageFile(file);
  };

  const handleSubmit = async () => {
    setSaving(true);
    setError('');
    try {
      let result;
      if (editingSlide) {
        result = await apiClient.updateHeroSlide(editingSlide.id, formData, imageFile);
      } else {
        result = await apiClient.addHeroSlide(formData, imageFile);
      }
      if (result && result.success) {
        handleCloseDialog();
        await loadSlides();
      } else {
        setError(result?.error || 'Failed to save slide');
      }
    } catch (e) {
      setError(e.message || 'Failed to save slide');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (slideId) => {
    if (!window.confirm('Are you sure you want to delete this slide?')) return;
    try {
      const result = await apiClient.deleteHeroSlide(slideId);
      if (result && result.success) {
        await loadSlides();
      } else {
        setError(result?.error || 'Failed to delete slide');
      }
    } catch (e) {
      setError(e.message || 'Failed to delete slide');
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Homepage Slider
        </Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenDialog()}>
          Add New Slide
        </Button>
      </Box>

      <Alert severity="info" sx={{ mb: 3 }}>
        These slides appear in the rotating banner at the top of the homepage. Order determines
        the sequence shown; inactive slides are hidden from the site.
      </Alert>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Order</TableCell>
                <TableCell>Title</TableCell>
                <TableCell>Subtitle</TableCell>
                <TableCell>Button</TableCell>
                <TableCell>Active</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {slides.map((slide) => (
                <TableRow key={slide.id}>
                  <TableCell>{slide.orderIndex}</TableCell>
                  <TableCell>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                      {slide.title}
                    </Typography>
                  </TableCell>
                  <TableCell>{slide.subtitle}</TableCell>
                  <TableCell>{slide.ctaText}</TableCell>
                  <TableCell>{slide.isActive ? 'Yes' : 'No'}</TableCell>
                  <TableCell>
                    <IconButton size="small" onClick={() => handleOpenDialog(slide)} color="primary">
                      <EditIcon />
                    </IconButton>
                    <IconButton size="small" onClick={() => handleDelete(slide.id)} color="error">
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {slides.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6}>
                    <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                      No slides yet.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>{editingSlide ? 'Edit Slide' : 'Add New Slide'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={8}>
              <TextField
                fullWidth
                label="Title"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                type="number"
                label="Order"
                name="orderIndex"
                value={formData.orderIndex}
                onChange={handleInputChange}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Subtitle"
                name="subtitle"
                value={formData.subtitle}
                onChange={handleInputChange}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                multiline
                rows={3}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Button Text"
                name="ctaText"
                value={formData.ctaText}
                onChange={handleInputChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Button Link"
                name="ctaLink"
                value={formData.ctaLink}
                onChange={handleInputChange}
                placeholder="/services"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Button variant="outlined" component="label">
                {imageFile ? 'Change Background Image' : 'Upload Background Image'}
                <input hidden accept="image/*" type="file" onChange={handleImageChange} />
              </Button>
              {imageFile && (
                <Typography variant="caption" sx={{ ml: 2 }}>
                  {imageFile.name}
                </Typography>
              )}
              {!imageFile && editingSlide?.image && (
                <Typography variant="caption" sx={{ ml: 2, display: 'block', mt: 1 }} color="text.secondary">
                  Keeping existing image unless a new one is uploaded.
                </Typography>
              )}
            </Grid>
            <Grid item xs={12} sm={6} sx={{ display: 'flex', alignItems: 'center' }}>
              <FormControlLabel
                control={<Switch name="isActive" checked={formData.isActive} onChange={handleSwitchChange} />}
                label="Active"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" disabled={saving}>
            {saving ? 'Saving...' : editingSlide ? 'Update Slide' : 'Add Slide'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ManageHeroSlides;
